howspace.components.fileUploader = (() => {
    'use strict';

    const getFileUploader = e => {
        if (e.target.classList.contains('howspace-file-uploader')) {
            return e.target;
        }
        return e.target.closest('.howspace-file-uploader') || null;
    };

    const allowsPaste = fileUploader => fileUploader.dataset.allowPaste !== 'false';

    // How long one backend step may run before the preview reassures the person that it is still working.
    const SLOW_STEP_MS = 8000;

    const slowNoteTimers = new WeakMap();

    const filesAwaitingScanConfirm = new WeakMap();

    const clearSlowNote = filePreview => {
        const timer = slowNoteTimers.get(filePreview);
        if (timer !== undefined) {
            clearTimeout(timer);
            slowNoteTimers.delete(filePreview);
        }

        const slowNote = filePreview.querySelector('.howspace-file-uploader-preview-slow-note');
        if (slowNote !== null) {
            slowNote.textContent = '';
            slowNote.classList.add('d-none');
        }
    };

    /**
     * The one place a preview's status changes. The state drives which indicator is visible: 'uploading'
     * shows the bar, 'working' shows the spinner, 'confirm', 'ready' and 'error' show neither.
     *
     * The status line is a live region, so while uploading it is hidden from assistive technology and the
     * progress bar reports the percentage instead. Announcing a number that changes many times a second
     * would bury the steps that actually carry news.
     */
    const setStatus = (filePreview, state, text) => {
        clearSlowNote(filePreview);
        filePreview.dataset.state = state;

        const statusText = filePreview.querySelector('.howspace-file-uploader-preview-progress-text');
        if (statusText !== null) {
            statusText.textContent = text;
            if (state === 'uploading') {
                statusText.setAttribute('aria-hidden', 'true');
            } else {
                statusText.removeAttribute('aria-hidden');
            }
        }

        if (state !== 'working') {
            return;
        }

        slowNoteTimers.set(filePreview, setTimeout(() => {
            const slowNote = filePreview.querySelector('.howspace-file-uploader-preview-slow-note');
            if (slowNote === null) {
                return;
            }
            slowNote.textContent = howspace.translate('fileuploader_progress_slow');
            slowNote.classList.remove('d-none');
        }, SLOW_STEP_MS));
    };

    const setError = (filePreview, text) => {
        setStatus(filePreview, 'error', text);
        filePreview.classList.add('has-error');
    };

    /**
     * A rejected file is more useful as a statement of what the uploader does take. Uploaders that accept
     * everything, or a mix of types, have no such noun worth printing and fall back to the generic wording.
     */
    const getWrongTypeText = fileUploader => {
        const allowedFileType = fileUploader.dataset.allowedFileType;
        if (!allowedFileType) {
            return howspace.translate('fileuploader_error_file_type_not_supported');
        }

        return howspace.translate('fileuploader_error_only_type_allowed', { file_type: allowedFileType });
    };

    // The backend tells us what it found inside the file, so the wait can name the thing that was uploaded.
    const getPreparingText = notification => {
        switch (notification.object.uploadedfile?.content?.type) {
            case 'audio':
                return howspace.translate('fileuploader_progress_preparing_audio');
            case 'image':
                return howspace.translate('fileuploader_progress_preparing_image');
            case 'video':
                return howspace.translate('fileuploader_progress_preparing_video');
            default:
                return howspace.translate('fileuploader_progress_preparing_file');
        }
    };

    const getFilePreview = notification => {
        const token = notification.object.uploadedfile?.upload_process_metadata?.token;
        return document.querySelector('.howspace-file-uploader-preview[data-upload-token="' + token + '"]');
    };

    const checkIfFileUploadCompleted = fileUploader => {
        const uploadedFiles = [];
        let fileUploadCompleted = true;
        fileUploader.querySelectorAll('.howspace-file-uploader-preview').forEach(filePreview => {
            if (filePreview.dataset.isStored === 'true') {
                uploadedFiles.push(JSON.parse(filePreview.dataset.uploadedFile));
                return;
            }
            fileUploadCompleted = false;
        });

        if (fileUploadCompleted) {
            howspace.dispatchEvent(fileUploader, howspace.components.fileUploader.UPLOAD_COMPLETED, {
                uploadedFiles: uploadedFiles
            });
        }
    };

    real.onTierReady(real.TIER_2, () => {
        real.events.on('uploadedfile:content_type_identified', notification => {
            const filePreview = getFilePreview(notification);
            if (filePreview === null) {
                // Did not find the file preview from the DOM.
                return;
            }

            setStatus(filePreview, 'working', getPreparingText(notification));
        });

        real.events.on('uploadedfile:stored', notification => {
            const filePreview = getFilePreview(notification);
            if (filePreview === null) {
                // Did not find the file preview from the DOM.
                return;
            }

            const fileUploader = filePreview.closest('.howspace-file-uploader');
            if (fileUploader === null) {
                // Did not find the file uploader from the DOM.
                return;
            }

            filePreview.dataset.isStored = true;
            filePreview.dataset.uploadedFile = JSON.stringify(notification.object.uploadedfile);
            filePreview.dataset.uploadedFileId = notification.object.uploadedfile.id;
            setStatus(filePreview, 'ready', howspace.translate('fileuploader_progress_ready'));

            checkIfFileUploadCompleted(fileUploader);
        });

        real.events.on('uploadedfile:invalid_content_type_type', notification => {
            const filePreview = getFilePreview(notification);
            if (filePreview === null) {
                // Did not find the file preview from the DOM.
                return;
            }

            const fileUploader = filePreview.closest('.howspace-file-uploader');
            if (fileUploader === null) {
                // Did not find the file uploader from the DOM.
                return;
            }

            setError(filePreview, getWrongTypeText(fileUploader));
        });

        real.events.on('uploadedfile:failed', notification => {
            const filePreview = getFilePreview(notification);
            if (filePreview === null) {
                // Did not find the file preview from the DOM.
                return;
            }

            // A virus finding arrives as a message the person can act on, so it wins over the generic wording.
            const errorMsg = notification.object.uploadedfile.upload_process_metadata?.error_msg;
            setError(filePreview, errorMsg || howspace.translate('fileuploader_error_preparing_failed'));
        });
    });

    const startFileUploads = (fileUploader, files) => {
        if (files.length > 1 && fileUploader.dataset.allowMultiple !== 'true') {
            // User is trying to upload multiple files, but only one is allowed.
            fileUploader.querySelector('.too-many-files-alert').classList.remove('d-none');
            return;
        }

        // Hide the form, show the progress.
        fileUploader.querySelector('.too-many-files-alert').classList.add('d-none');
        fileUploader.querySelector('.howspace-file-uploader-form').classList.add('d-none');
        fileUploader.querySelector('.howspace-file-uploader-progress').classList.remove('d-none');

        // Loop through all files and append previews.
        Array.prototype.forEach.call(files, file => {
            appendFilePreview(fileUploader, file);
        });
    };

    // Whether the file matches an `accept` list, by extension or by content type. An empty list allows everything.
    const isTypeAllowed = (allowedContentTypes, file) => {
        if (!allowedContentTypes) {
            return true;
        }

        const fileType = file.type || '';
        const fileName = (file.name || '').toLowerCase();
        let isAllowed = false;
        allowedContentTypes.split(',').forEach((contentType) => {
            contentType = contentType.trim().toLowerCase();
            if (contentType.startsWith('.')) {
                if (fileName.endsWith(contentType)) {
                    isAllowed = true;
                }
                return;
            }
            if (contentType.endsWith('*')) {
                // Content type ends with asterisk (*), in this case it's enough if the fileType
                // begins with the content type (disregarding the * at the end).
                if (fileType.toLowerCase().startsWith(contentType.slice(0, -1))) {
                    isAllowed = true;
                }
            } else if (fileType.toLowerCase() === contentType) {
                isAllowed = true;
            }
        });
        return isAllowed;
    };

    const isFileTypeAllowed = (fileUploader, file) =>
        isTypeAllowed(fileUploader.querySelector('.howspace-file-input')?.getAttribute('accept') || '', file);

    const isTooLargeToScan = (fileUploader, file) => {
        const virusScanMaxFileSize = fileUploader.dataset.virusScanMaxFileSize;
        return virusScanMaxFileSize !== undefined && file.size > Number(virusScanMaxFileSize);
    };

    const showFileName = (filePreview, file) => {
        const fileName = document.createElement('div');
        fileName.id = filePreview.id + '-filename';
        fileName.className = 'howspace-file-uploader-preview-filename break-word';
        fileName.textContent = file.name;
        filePreview.querySelector('.howspace-file-uploader-preview-content').prepend(fileName);
    };

    const askToUploadWithoutScan = (fileUploader, filePreview, file) => {
        const template = fileUploader.querySelector('template.howspace-file-uploader-scan-confirm');
        filesAwaitingScanConfirm.set(filePreview, file);
        setStatus(filePreview, 'confirm', '');
        showFileName(filePreview, file);
        filePreview.querySelector('.howspace-file-uploader-preview-content')
            .insertAdjacentHTML('beforeend', howspace.template.getHtml(template));

        const scanConfirm = filePreview.querySelector('.howspace-file-uploader-scan-confirm');
        const title = scanConfirm.querySelector('.howspace-file-uploader-scan-confirm-title');
        title.id = filePreview.id + '-scan-title';
        scanConfirm.setAttribute('aria-labelledby', title.id + ' ' + filePreview.id + '-filename');
        focusNextScanConfirm(fileUploader);
    };

    const focusNextScanConfirm = (fileUploader, fallback = null) => {
        const next = fileUploader.querySelector('.howspace-file-uploader-scan-confirm .howspace-file-scan-cancel-btn');
        (next ?? fallback)?.focus();
    };

    const appendFilePreview = (fileUploader, file) => {
        // Append a new file upload preview element.
        const progress = fileUploader.querySelector('.howspace-file-uploader-progress');
        const uploadToken = real.uid();
        const uid = 'howspace-file-upload-' + uploadToken;
        const deleteBtn = howspace.template.getHtml(fileUploader.querySelector('template.howspace-file-delete-btn'));
        const spinner = howspace.template.getHtml(fileUploader.querySelector('template.howspace-file-uploader-spinner'));
        const filePreview = document.createElement('div');
        filePreview.id = uid;
        filePreview.dataset.source = fileUploader.dataset.source;
        filePreview.dataset.uploadToken = uploadToken;
        filePreview.dataset.isStored = false;
        filePreview.dataset.state = 'uploading';
        filePreview.classList.add('howspace-file-uploader-preview');
        filePreview.innerHTML = `
            <div class="howspace-file-uploader-preview-icon"></div>
            <div class="howspace-file-uploader-preview-content">
                <div class="howspace-file-uploader-preview-progress-bar-bg" role="progressbar"
                    aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
                    <div class="howspace-file-uploader-preview-progress-bar"></div>
                </div>
                <div class="howspace-file-uploader-preview-status">
                    <span class="howspace-file-uploader-preview-spinner" aria-hidden="true">${spinner}</span>
                    <span class="howspace-file-uploader-preview-progress-text" aria-live="polite"
                        aria-hidden="true"></span>
                </div>
                <div class="howspace-file-uploader-preview-slow-note d-none" aria-live="polite"></div>
            </div>
            <div class="howspace-file-uploader-preview-delete self-center px-3">${deleteBtn}</div>
        `;
        progress.appendChild(filePreview);

        filePreview.querySelector('.howspace-file-uploader-preview-progress-bar-bg')
            .setAttribute('aria-label', howspace.translate('fileuploader_upload_progress_label'));

        const previewIcon = filePreview.querySelector('.howspace-file-uploader-preview-icon');

        if (!isFileTypeAllowed(fileUploader, file)) {
            // File content type is not allowed.
            setError(filePreview, getWrongTypeText(fileUploader));
        } else if (file.size > fileUploader.dataset.maxFileSize) {
            // File is too big.
            setError(filePreview, howspace.translate('fileuploader_error_too_big_file', {
                max: fileUploader.dataset.humanReadableMaxFileSize
            }));
        } else if (isTooLargeToScan(fileUploader, file) && fileUploader.dataset.canSkipVirusScan === 'true') {
            askToUploadWithoutScan(fileUploader, filePreview, file);
        } else if (isTooLargeToScan(fileUploader, file)) {
            filePreview.classList.add('is-too-large-to-scan');
            showFileName(filePreview, file);
            setError(filePreview, howspace.translate('fileuploader_error_too_large_to_scan', {
                max: fileUploader.dataset.humanReadableVirusScanMaxFileSize
            }));
        } else {
            // No errors, start uploading the file.
            setStatus(filePreview, 'uploading', howspace.translate('fileuploader_progress_uploading', {
                percentage: '0%'
            }));
            startSingleFileUpload(fileUploader, file, uploadToken);
        }

        if (file.type.startsWith('image/')) {
            // Image.
            const reader = new FileReader();
            reader.onload = e => {
                previewIcon.style.backgroundImage = `url("${e.target.result}")`;
            };
            reader.readAsDataURL(file);
        } else if (file.type.startsWith('video/')) {
            // Video.
            previewIcon.innerHTML = `
                <video>
                    <source type="${file.type}" src="${URL.createObjectURL(file)}">
                </video>
            `;
        } else if (file.type === 'text/plain') {
            // Plain text.
            previewIcon.innerHTML = fileUploader.querySelector('.file-type-icons .file-type-text').outerHTML;
        } else if (file.type === 'application/pdf') {
            // PDF.
            previewIcon.innerHTML = fileUploader.querySelector('.file-type-icons .file-type-pdf').outerHTML;
        } else {
            // Generic file type.
            previewIcon.innerHTML = fileUploader.querySelector('.file-type-icons .file-type-other').outerHTML;
        }
    };

    const getErrorText = (fileUploader, response) => {
        /**
         * https://www.php.net/manual/en/features.file-upload.errors.php
         *
         * 0 => 'There is no error, the file uploaded with success',
         * 1 => 'The uploaded file exceeds the upload_max_filesize directive in php.ini',
         * 2 => 'The uploaded file exceeds the MAX_FILE_SIZE directive that was specified in the HTML form',
         * 3 => 'The uploaded file was only partially uploaded',
         * 4 => 'No file was uploaded',
         * 6 => 'Missing a temporary folder',
         * 7 => 'Failed to write file to disk.',
         * 8 => 'A PHP extension stopped the file upload.',
         */

        if (response.status === 0) {
            // Upload failed due to a network error.
            return howspace.translate('fileuploader_error_network_failed');
        }

        let text = null;
        real.ajax.loopErrors(response, error => {
            const errorCode = parseInt(error.key.split('_').pop());

            if (error.key === 'filename_too_long') {
                text = howspace.translate('fileuploader_error_filename_too_long', {
                    max: fileUploader.dataset.maxFilenameLength
                });
            } else if (error.key === 'file_too_large_to_scan' || error.key === 'formula_check_failed') {
                text = error.message;
            } else if (errorCode === 1 || errorCode === 2) {
                // User uploaded a file that is too big.
                text = howspace.translate('fileuploader_error_too_big_file', {
                    max: fileUploader.dataset.humanReadableMaxFileSize
                });
            } else {
                text = howspace.translate('fileuploader_error_save_failed');
            }
        });

        return text ?? howspace.translate('fileuploader_unknown_error');
    };

    const startSingleFileUpload = (fileUploader, file, uploadToken, skipVirusScan = false) => {
        const formData = new FormData();

        // Put max file size to the form data.
        const maxFileSize = fileUploader.dataset.maxFileSize;
        if (maxFileSize) {
            formData.append('MAX_FILE_SIZE', maxFileSize);
        }

        // Append the upload token to the form data.
        const uploadProcessMetadata = { token: uploadToken };
        if (skipVirusScan) {
            uploadProcessMetadata.skip_virus_scan = true;
        }
        formData.append('upload_process_metadata', JSON.stringify(uploadProcessMetadata));

        // Append the file to the form data.
        formData.append('file', file);

        // Get URL from data attributes or use default fallback.
        const uri = fileUploader.dataset.uri || '/api/uploadedfiles';

        real.ajax(real.baseUri + uri, {
            type: 'POST',
            data: formData,
            dataType: 'json',
            contentType: false,
            processData: false,
            xhr: () => {
                const xhr = new XMLHttpRequest();

                xhr.upload.addEventListener('progress', e => {
                    const preview = fileUploader.querySelector('#howspace-file-upload-' + uploadToken);
                    if (!e.lengthComputable || preview === null) {
                        // The file was removed while it was still uploading.
                        return;
                    }

                    const percentage = Math.floor(e.loaded / e.total * 100);
                    preview.querySelector('.howspace-file-uploader-preview-progress-bar').style.width =
                        percentage + '%';
                    preview.querySelector('.howspace-file-uploader-preview-progress-bar-bg')
                        .setAttribute('aria-valuenow', percentage);
                    setStatus(preview, 'uploading', howspace.translate('fileuploader_progress_uploading', {
                        percentage: percentage + '%'
                    }));
                }, false);

                return xhr;
            },
            success: () => {
                const filePreview = fileUploader.querySelector('#howspace-file-upload-' + uploadToken);
                if (filePreview === null || filePreview.dataset.state !== 'uploading') {
                    // The file was removed, or a later step already arrived over the websocket.
                    return;
                }

                setStatus(filePreview, 'working', howspace.translate('fileuploader_progress_checking'));
            },
            logError: false,
            showErrorNotification: false,
            error: response => {
                const filePreview = fileUploader.querySelector('#howspace-file-upload-' + uploadToken);
                if (filePreview === null) {
                    // The file was removed while it was still uploading.
                    return;
                }

                setError(filePreview, getErrorText(fileUploader, response));
                checkIfFileUploadCompleted(fileUploader);
            }
        });
    };

    const handleClick = e => {
        // "Click" the file input.
        getFileUploader(e).querySelector('.howspace-file-input').click();
    };

    const handleChange = e => {
        const fileUploader = getFileUploader(e);
        startFileUploads(fileUploader, e.target.files);
    };

    const handleDrag = e => {
        e.preventDefault();
    };

    const handleDragOver = e => {
        const form = getFileUploader(e).querySelector('.howspace-file-uploader-form');
        if (!form.classList.contains('uploading')) {
            form.classList.add('drag-over');
        }
    };

    const handleDragLeave = e => {
        const form = getFileUploader(e).querySelector('.howspace-file-uploader-form');
        form.classList.remove('drag-over');
    };

    const handleDrop = e => {
        const fileUploader = getFileUploader(e);
        const form = fileUploader.querySelector('.howspace-file-uploader-form');
        form.classList.remove('drag-over');
        startFileUploads(fileUploader, e.dataTransfer.files);
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const fileUploader = getFileUploader(e);
        const items = e.clipboardData.items;
        const files = [];
        for (let index in items) {
            const item = items[index];
            if (item.kind === 'file') {
                files.push(item.getAsFile());
            }
        }
        if (files.length > 0) {
            startFileUploads(fileUploader, files);
        }
    };

    return {
        UPLOAD_COMPLETED: 'howspace-file-uploader-upload-completed',
        init: elem => {
            elem.findIncludingSelf('.howspace-file-uploader:not(.howspace-file-uploader-initialized)').each(function () {
                const fileUploader = this;
                const $fileUploader = $(this);

                fileUploader.classList.add('howspace-file-uploader-initialized');

                if (howspace.isMobile()) {
                    fileUploader.classList.add('is-mobile');
                }

                const removePreview = preview => {
                    const progress = preview.closest('.howspace-file-uploader-progress');
                    clearSlowNote(preview);
                    filesAwaitingScanConfirm.delete(preview);
                    preview.remove();
                    if (progress.querySelector('.howspace-file-uploader-preview:not(.has-error)') === null) {
                        // No more valid files left.
                        howspace.components.fileUploader.reset($fileUploader);
                        $fileUploader.find('input[type="file"].howspace-file-input').val('');
                        return false;
                    }
                    return true;
                };

                $fileUploader.on('click', '.howspace-file-delete-btn', (e) => {
                    removePreview(e.currentTarget.closest('.howspace-file-uploader-preview'));
                });

                $fileUploader.on('click', '.howspace-file-scan-cancel-btn', (e) => {
                    if (!removePreview(e.currentTarget.closest('.howspace-file-uploader-preview'))) {
                        fileUploader.querySelector('.howspace-file-button')?.focus();
                        return;
                    }
                    checkIfFileUploadCompleted(fileUploader);
                    const firstPreview = fileUploader.querySelector('.howspace-file-uploader-preview');
                    if (firstPreview !== null) {
                        firstPreview.tabIndex = -1;
                    }
                    focusNextScanConfirm(fileUploader, firstPreview);
                });

                $fileUploader.on('click', '.howspace-file-scan-upload-btn', (e) => {
                    const preview = e.currentTarget.closest('.howspace-file-uploader-preview');
                    const file = filesAwaitingScanConfirm.get(preview);
                    if (file === undefined) {
                        return;
                    }
                    filesAwaitingScanConfirm.delete(preview);
                    preview.querySelector('.howspace-file-uploader-scan-confirm')?.remove();
                    setStatus(preview, 'uploading', howspace.translate('fileuploader_progress_uploading', {
                        percentage: '0%'
                    }));
                    startSingleFileUpload(fileUploader, file, preview.dataset.uploadToken, true);
                    preview.tabIndex = -1;
                    focusNextScanConfirm(fileUploader, preview);
                });

                // Put focus to the input.
                if (elem.findIncludingSelf('.howspace-file-uploader').length === 1) {
                    fileUploader.querySelector('.howspace-form-text-input')?.focus();
                }

                // When clicking the button -> trigger click on the file input.
                const fileButton = fileUploader.querySelector('.howspace-file-button');
                fileButton.addEventListener('click', handleClick);

                // Browsing files.
                const fileInput = fileUploader.querySelector('.howspace-file-input');
                fileInput.addEventListener('change', handleChange);

                // Pasting files.
                if (allowsPaste(fileUploader)) {
                    fileUploader.addEventListener('paste', handlePaste);
                }

                // Drag events.
                const form = fileUploader.querySelector('.howspace-file-uploader-form');
                ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(dragEvent => {
                    form.addEventListener(dragEvent, handleDrag);
                });

                // Drag over events.
                ['dragover', 'dragenter'].forEach(dragOverEvent => {
                    form.addEventListener(dragOverEvent, handleDragOver);
                });

                // Drag leave events.
                ['dragleave', 'dragend'].forEach(dragLeaveEvent => {
                    form.addEventListener(dragLeaveEvent, handleDragLeave);
                });

                // Drop.
                form.addEventListener('drop', handleDrop);
            });
        },
        destroy: elem => {
            elem.findIncludingSelf('.howspace-file-uploader.howspace-file-uploader-initialized').each(function () {
                const fileUploader = this;

                fileUploader.classList.remove('howspace-file-uploader-initialized');

                // When clicking the button -> trigger click on the file input.
                const fileButton = fileUploader.querySelector('.howspace-file-button');
                fileButton.removeEventListener('click', handleClick);

                // Browsing files.
                const fileInput = fileUploader.querySelector('.howspace-file-input');
                fileInput.removeEventListener('change', handleChange);

                // Pasting files.
                if (allowsPaste(fileUploader)) {
                    fileUploader.removeEventListener('paste', handlePaste);
                }

                // Drag events.
                const form = fileUploader.querySelector('.howspace-file-uploader-form');
                ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(dragEvent => {
                    form.removeEventListener(dragEvent, handleDrag);
                });

                // Drag over events.
                ['dragover', 'dragenter'].forEach(dragOverEvent => {
                    form.removeEventListener(dragOverEvent, handleDragOver);
                });

                // Drag leave events.
                ['dragleave', 'dragend'].forEach(dragLeaveEvent => {
                    form.removeEventListener(dragLeaveEvent, handleDragLeave);
                });

                // Drop.
                form.removeEventListener('drop', handleDrop);
            });
        },
        reset : elem => {
            elem.findIncludingSelf('.howspace-file-uploader').each(function () {
                const fileUploader = this;

                fileUploader.querySelectorAll('.howspace-file-uploader-preview').forEach(clearSlowNote);

                fileUploader.querySelector('.too-many-files-alert').classList.add('d-none');
                fileUploader.querySelector('.howspace-file-uploader-form').classList.remove('d-none');
                fileUploader.querySelector('.howspace-file-uploader-progress').classList.add('d-none');
                fileUploader.querySelector('.howspace-file-uploader-progress').innerHTML = '';
                fileUploader.querySelector('.howspace-file-input').value = '';
            });
        },
        startFileUploads: startFileUploads,
        isTypeAllowed: isTypeAllowed,
    };

})();
